import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../../../shared/components/Button.js';
import { ColumnFilters, type FilterableColumn } from '../../../../shared/components/ColumnFilters.js';
import {
  listBirthRights,
  listBirthRightChoices,
  listBirthRightDrift,
  grantBirthRight,
  revokeBirthRight,
  type RoleBirthRights,
  type BirthRightChoices,
  type BirthRightDrift,
} from '../../api/rolesApi.js';

/**
 * What every tenant created from now on is born holding.
 *
 * This is the platform's template (t017c), **not** a tenant's settings. The
 * Role Rights screen decides what a role may do inside one tenant; this decides
 * what a role starts with in every tenant made after today, which the
 * provisioning seed reads.
 *
 * The difference matters and the two look alike, so the screen says it in
 * words rather than leaving it to be inferred: saving here changes tenants that
 * do not exist yet, and never one that does.
 *
 * Why it exists at all: until m02_116 this mapping was a typed list inside a
 * plpgsql function, so a right added by a new module reached new tenants only
 * if somebody remembered to edit that function. Nobody did, and a Branding
 * Officer spent weeks unable to act on his own screen because of it.
 */
interface Row {
  readonly roleCode: string;
  readonly roleName: string | null;
  readonly permissionCode: string;
  readonly permissionName: string | null;
  readonly resourceType: string;
  readonly module: string;
}

/** The module a right belongs to, taken from the code rather than a lookup. */
function moduleOf(permissionCode: string): string {
  const prefix = permissionCode.split('_')[0];
  return /^M\d+$/.test(prefix) ? prefix : 'GENERAL';
}

function flatten(groups: readonly RoleBirthRights[]): readonly Row[] {
  return groups.flatMap((g) =>
    g.permissions.map((p) => ({
      roleCode: g.roleCode,
      roleName: g.roleName,
      permissionCode: p.permissionCode,
      permissionName: p.permissionName,
      resourceType: p.resourceType,
      module: moduleOf(p.permissionCode),
    })),
  );
}

export function RoleBirthRightsPage(): ReactNode {
  const { token } = useAuth();

  const [rows, setRows] = useState<readonly Row[] | null>(null);
  const [choices, setChoices] = useState<BirthRightChoices | null>(null);
  const [shown, setShown] = useState<readonly Row[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // What to add. Both lists come from the catalogues a tenant is built from, so
  // the screen cannot offer a role or a right that would never match anything.
  const [addRole, setAddRole] = useState('');
  const [addPermission, setAddPermission] = useState('');
  const [addScope, setAddScope] = useState('GLOBAL');
  /** Where each live tenant stands against this template. */
  const [drift, setDrift] = useState<readonly BirthRightDrift[] | null>(null);

  const reload = async () => {
    const [groups, ch, dr] = await Promise.all([
      listBirthRights(token),
      listBirthRightChoices(token),
      listBirthRightDrift(token),
    ]);
    setRows(flatten(groups));
    setChoices(ch);
    setDrift(dr);
  };

  useEffect(() => {
    let alive = true;
    Promise.all([
      listBirthRights(token),
      listBirthRightChoices(token),
      listBirthRightDrift(token),
    ])
      .then(([groups, ch, dr]) => {
        if (!alive) return;
        setRows(flatten(groups));
        setChoices(ch);
        setDrift(dr);
      })
      .catch((e) => { if (alive) { setRows([]); setError((e as Error).message); } });
    return () => { alive = false; };
  }, [token]);

  const filterColumns = useMemo<ReadonlyArray<FilterableColumn<Row>>>(
    () => [
      { key: 'role', label: 'Role', valueOf: (r) => r.roleCode },
      { key: 'module', label: 'Module', valueOf: (r) => r.module },
      { key: 'scope', label: 'Scope', valueOf: (r) => r.resourceType },
    ],
    [],
  );

  const add = async () => {
    if (!addRole || !addPermission) return;
    setBusy(true); setError(null); setStatus(null);
    try {
      await grantBirthRight(addRole, addPermission, addScope, token);
      await reload();
      setStatus(`${addRole} will now be born holding ${addPermission}.`);
      setAddPermission('');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const remove = async (r: Row) => {
    setBusy(true); setError(null); setStatus(null);
    try {
      await revokeBirthRight(r.roleCode, r.permissionCode, token);
      await reload();
      setStatus(`${r.roleCode} will no longer be born holding ${r.permissionCode}.`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  if (!rows) return <p>Loading…</p>;

  const visible = shown ?? rows;

  return (
    <main aria-label="role-birth-rights">
      <h1>What a role is born with</h1>
      <p>
        This is the template every tenant created from now on is built from.
        Changing it affects tenants that do not exist yet — it never changes a
        tenant that already does. To change what a role may do inside this
        tenant, use <strong>Role Rights</strong>.
      </p>

      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      {/*
        Where each live tenant stands against this template.

        Nobody could ask this before, and that is how a Branding Officer missing
        two of his rights stayed invisible for weeks. The two columns mean
        different things and the screen says so, because acting on them wrongly
        would overwrite a tenant's own decisions:

          missing — the template offers it and the tenant does not hold it yet.
                    It arrives by itself on the next reconciliation.
          beyond  — the tenant granted it itself, on its own Role Rights
                    screen. That is its business, not the template's.
      */}
      <section aria-label="tenant-drift">
        <h2>Where each tenant stands</h2>
        {drift === null ? (
          <p>Measuring…</p>
        ) : drift.length === 0 ? (
          <p data-testid="drift-none">Every tenant matches the template exactly.</p>
        ) : (
          <table aria-label="drift-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Not yet granted</th>
                <th>Granted by the tenant itself</th>
              </tr>
            </thead>
            <tbody>
              {drift.map((d) => (
                <tr key={d.tenantId} data-testid={`drift-${d.tenantCode}`}>
                  <td>{d.tenantCode}</td>
                  <td data-testid={`drift-missing-${d.tenantCode}`}>
                    {d.missing.length === 0 ? '—' : d.missing.length}
                    {d.missing.length > 0 ? (
                      <details>
                        <summary>show</summary>
                        <ul>
                          {d.missing.map((m) => (
                            <li key={`${m.roleCode}-${m.permissionCode}`}>
                              {m.roleCode} — {m.permissionCode}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </td>
                  <td data-testid={`drift-beyond-${d.tenantCode}`}>
                    {d.beyond.length === 0 ? '—' : d.beyond.length}
                    {d.beyond.length > 0 ? (
                      <details>
                        <summary>show</summary>
                        <ul>
                          {d.beyond.map((b) => (
                            <li key={`${b.roleCode}-${b.permissionCode}`}>
                              {b.roleCode} — {b.permissionCode}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p data-testid="drift-rule">
          An addition reaches every tenant by itself. A right taken out of the
          template is <strong>not</strong> taken away from tenants that already
          hold it — a tenant may have granted it deliberately, and the template
          does not overrule a tenant's own decision.
        </p>
      </section>

      <section aria-label="add-birth-right">
        <h2>Give a role a right at birth</h2>
        <label htmlFor="brr-role">Role</label>
        <select
          id="brr-role"
          aria-label="birth-role"
          value={addRole}
          onChange={(e) => setAddRole(e.target.value)}
        >
          <option value="">Select…</option>
          {(choices?.roles ?? []).map((r) => (
            <option key={r.roleCode} value={r.roleCode}>
              {r.roleName ? `${r.roleCode} — ${r.roleName}` : r.roleCode}
            </option>
          ))}
        </select>

        <label htmlFor="brr-permission">Right</label>
        <select
          id="brr-permission"
          aria-label="birth-permission"
          value={addPermission}
          onChange={(e) => setAddPermission(e.target.value)}
        >
          <option value="">Select…</option>
          {(choices?.permissions ?? []).map((p) => (
            <option key={p.permissionCode} value={p.permissionCode}>
              {p.permissionName ? `${p.permissionCode} — ${p.permissionName}` : p.permissionCode}
            </option>
          ))}
        </select>

        {/*
          Part of a grant's identity in t017, not decoration: the same pair at a
          different scope is a second row. Offered rather than assumed, and
          defaulted to what most of the template already uses.
        */}
        <label htmlFor="brr-scope">Scope</label>
        <select
          id="brr-scope"
          aria-label="birth-scope"
          value={addScope}
          onChange={(e) => setAddScope(e.target.value)}
        >
          <option value="GLOBAL">GLOBAL</option>
          <option value="*">*</option>
        </select>

        <Button
          type="button"
          variant="primary"
          onClick={() => void add()}
          disabled={busy || !addRole || !addPermission}
          data-testid="add-birth-right"
        >
          Add to the template
        </Button>
      </section>

      <ColumnFilters
        ariaLabel="birth-right-filters"
        columns={filterColumns}
        rows={rows}
        onChange={setShown}
      />

      <div style={{ overflowX: 'auto' }}>
        <table aria-label="birth-rights">
          <thead>
            <tr>
              <th>Role</th><th>Module</th><th>Right</th><th>Scope</th><th />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} data-testid="no-birth-rights">
                  {rows.length === 0
                    ? 'The template is empty — a new tenant would be born with no rights at all.'
                    : 'No row matches the filters.'}
                </td>
              </tr>
            ) : null}
            {visible.map((r) => (
              <tr key={`${r.roleCode}/${r.permissionCode}`}>
                <td>{r.roleCode}</td>
                <td>{r.module}</td>
                <td>
                  {r.permissionCode}
                  {r.permissionName ? ` — ${r.permissionName}` : ''}
                </td>
                <td>{r.resourceType}</td>
                <td>
                  <Button
                    type="button"
                    variant="subtle"
                    onClick={() => void remove(r)}
                    disabled={busy}
                    data-testid={`remove-${r.roleCode}-${r.permissionCode}`}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
